import { getAnimationData } from "lib/animatedElementUtils";
import {
  applyPresetToAllPages,
  applyPresetToPage,
  getPresetById
} from "lib/animationPresets";
import { updateDurationForDesign } from "lib/designUtils";
import Logger from "lib/logger";
// Direct import (not via the editorOps barrel) to avoid a circular
// dependency: EditorResizeOps imports ActionBarOps for the geometry
// propagation wire, and the barrel would pull EditorResizeOps back through
// ActionBarOps. Live-binding ESM tolerates the cycle at runtime, but direct
// imports keep the module graph acyclic.
import EditorElementOps from "views/components/Editor/editorOps/EditorElementsOps";
import { cloneDeep } from "lib";
import {
  ANIMATION_DURATION,
  calculateTotalAnimationDuration
} from "lib/animationUtils";
import { PROPAGATABLE_ATTRIBUTES_BY_TYPE } from "lib/editorActionPropagation/propagatableAttributes";

/**
 * A file containing the functions that are used across multiple, if not all, action bar containers.
 */
const ActionBarOps = {
  onElementsAttributesChange(elementUpdates, containerProps, callback) {
    Logger.info("ActionBarOps.onElementsAttributesChange called");
    const updatedDesignData =
      containerProps.designData.updateElementsAttributes({
        elementUpdates
      });

    const isCssAnimationUpdate = Object.values(elementUpdates).some(update =>
      update.hasOwnProperty("cssAnimation")
    );

    if (isCssAnimationUpdate) {
      this.handleCssAnimationUpdate(
        elementUpdates,
        updatedDesignData,
        containerProps
      );
      this.enqueueCssAnimationPropagation(
        elementUpdates,
        updatedDesignData,
        containerProps
      );
      return;
    }

    containerProps.updateContextState(updatedDesignData);
    containerProps.onSave(updatedDesignData, {});
    // TODO: What to do with the callback?
  },

  /**
   * Handle CSS animation updates for elements
   * @param {object} elementUpdates - The updates to apply to elements
   * @param {object} updatedDesignData - The design data with initial updates applied
   * @param {object} containerProps - Props of the calling container
   */
  handleCssAnimationUpdate(elementUpdates, updatedDesignData, containerProps) {
    const { designData } = containerProps;
    const pagesUpdated = cloneDeep(updatedDesignData.pages);
    const animatedUpdates = Object.values(elementUpdates).filter(update =>
      update.hasOwnProperty("cssAnimation")
    );

    animatedUpdates.forEach(element => {
      const elementData = updatedDesignData.elements[element.uniqueId];
      const pageAnimatedElements =
        pagesUpdated[element.pageId].animatedElements;

      const animationData = getAnimationData({
        ...elementData,
        pageId: element.pageId
      });

      const elementAlreadyHasAnimation = animationData.some(
        dataPoint => dataPoint.animationDataKey in pageAnimatedElements
      );

      if (elementAlreadyHasAnimation) {
        if (
          !element.cssAnimation ||
          (Array.isArray(element.cssAnimation) &&
            element.cssAnimation.length === 0)
        ) {
          // Element has no animations left, remove it from animatedElements
          animationData.forEach(dataPoint => {
            delete pagesUpdated[element.pageId].animatedElements[
              dataPoint.animationDataKey
            ];
          });
        } else {
          // need to handle the update of animation data
          // For textbox elements with letter/word animations, calculate the proper duration
          let cssAnimationDuration;
          if (
            elementData.type === "textbox" &&
            element.cssAnimation.textType &&
            (element.cssAnimation.textType === "letter" ||
              element.cssAnimation.textType === "word")
          ) {
            // Use calculateTotalAnimationDuration for letter/word animations
            cssAnimationDuration = calculateTotalAnimationDuration([
              { ...elementData, cssAnimation: element.cssAnimation }
            ]);
          } else {
            // Use default duration for other animation types
            cssAnimationDuration =
              element.cssAnimation.delay + ANIMATION_DURATION;
          }

          Object.values(pageAnimatedElements).forEach(dataPoint => {
            if (
              dataPoint.uniqueId === element.uniqueId &&
              cssAnimationDuration > dataPoint.duration
            ) {
              pagesUpdated[element.pageId].animatedElements[
                dataPoint.animationDataKey
              ].duration = cssAnimationDuration;
            }
          });
        }
      } else {
        // need to handle the addition of new animation data
        // Only add to animatedElements if the element actually has animations
        if (
          element.cssAnimation &&
          (!Array.isArray(element.cssAnimation) ||
            element.cssAnimation.length > 0)
        ) {
          animationData.forEach(dataPoint => {
            pagesUpdated[element.pageId].animatedElements[
              dataPoint.animationDataKey
            ] = dataPoint;
          });
        }
      }
    });

    updatedDesignData.pages = pagesUpdated;

    const designWithUpdatedDuration = updateDurationForDesign(
      updatedDesignData,
      designData
    );
    containerProps.updateContextState(designWithUpdatedDuration);
    containerProps.onSave(designWithUpdatedDuration, {});
  },

  /**
   * Apply an animation preset to elements on the current page or all pages.
   * Produces a single atomic update for undo support.
   *
   * @param {string|null} presetId - The preset ID to apply, or null to clear all animations
   * @param {"page"|"all-pages"} scope - Whether to apply to current page or all pages
   * @param {string} currentPageId - The current page ID (used when scope is "page")
   * @param {object} containerProps - Props containing designData, updateContextState, onSave
   */
  onApplyAnimationPreset(presetId, scope, currentPageId, containerProps) {
    Logger.info(
      `ActionBarOps.onApplyAnimationPreset called: preset=${presetId}, scope=${scope}`
    );

    const { designData } = containerProps;
    let allElementUpdates = {};

    if (!presetId) {
      // Clear all animations
      const pageIds =
        scope === "all-pages" ? designData.pagesOrder : [currentPageId];

      for (const pageId of pageIds) {
        const page = designData.pages[pageId];
        if (!page) continue;
        for (const elementId of page.elementsOrder) {
          const element = designData.elements[elementId];
          if (element) {
            allElementUpdates[elementId] = {
              uniqueId: elementId,
              pageId,
              cssAnimation: []
            };
          }
        }
      }
    } else {
      const preset = getPresetById(presetId);
      if (!preset) {
        Logger.info(
          `ActionBarOps.onApplyAnimationPreset: preset not found: ${presetId}`
        );
        return;
      }

      if (scope === "all-pages") {
        const results = applyPresetToAllPages(preset, designData);
        for (const result of results) {
          Object.assign(allElementUpdates, result.elementUpdates);
        }
      } else {
        const result = applyPresetToPage(preset, currentPageId, designData);
        allElementUpdates = result.elementUpdates;
      }
    }

    if (Object.keys(allElementUpdates).length === 0) return;

    this.onElementsAttributesChange(allElementUpdates, containerProps);
  },

  /**
   * Take the current selection and select grouped elements instead of groups where applicable
   * @param {object} containerProps - Props of the calling container.
   */
  onFlattenSelection(containerProps) {
    Logger.info("ActionBarOps.onFlattenSelection called");
    const newSelection = EditorElementOps.flattenSelection(
      containerProps.selectedItems,
      containerProps.designData
    );
    containerProps.updateContextState({ selectedItems: newSelection });
  },

  /**
   * Update the supplied attributes of the selected items.
   *
   * @param attributes - The attributes of the text that are to be changed.
   * @param selectedItems - The selected text items
   * @param containerProps - The container props containing additional data required to process
   * the action.
   * @param callback - Callback function to execute on completion.
   * @returns {object} updated design data object.
   */
  onSelectedElementsAttributesChange(
    attributes,
    selectedItems,
    containerProps,
    callback
  ) {
    Logger.info("ActionbarOps.onSelectedElementsAttributesChange called");

    const { designData } = containerProps;
    const updatedDesignData = designData.updateElementsAttribute(
      {
        elementsId: selectedItems.map(item => item.itemId),
        attributes
      },
      callback
    );

    const updatedContextState = {
      designData: updatedDesignData,
      lastAttributeChanges: selectedItems.map(({ itemId, pageId }) => ({
        attributes,
        uniqueId: itemId,
        pageId,
        originalElement: designData.elements[itemId]
      })),
      elementPreviews: {},
      selectedItems: containerProps.selectedItems.map(item => ({
        ...item,
        preview: {}
      }))
    };

    containerProps.onSave(updatedDesignData, updatedContextState);
    containerProps.updateContextState(updatedContextState);

    // Some callers require this to add to update collection queue
    return updatedDesignData;
  },

  /**
   * Shared enqueue helper for "Update Collection" attribute propagation. Each
   * action bar container's addToUpdateCollectionQueue delegates here so the
   * gating, homogeneity check, and allowlist filter are in one place.
   *
   * Callers pass the **post-update** designData, so `selectedElementsData[i]`
   * spreads an element whose attribute values already reflect the change the
   * user just made. That is fine for the current allowlist (opacity / angle /
   * blendMode / transform / shadow / colors don't participate in
   * matchElementInDesign's content-based fallback at
   * collectionHelpers.js:61-79). If attributes that DO participate in that
   * fallback (`value`, `src`, `imageInstructions`) are added to the allowlist,
   * callers must snapshot the pre-update element data before invoking this
   * helper — otherwise sibling matching will silently fail on divergent
   * collections.
   *
   * @param {object} designData - the current design data (pre- or post-save).
   * @param {Array<{itemId: string, pageId: string}>} selectedItems
   * @param {object} attributes - raw attributes object from the caller.
   * @param {Array} collectionDesigns - sibling designs in the same collection.
   * @param {Function} addActionToPropagationQueue - bound dispatch wrapper.
   */
  enqueueAttributePropagation({
    designData,
    selectedItems,
    attributes,
    collectionDesigns,
    addActionToPropagationQueue
  }) {
    if (!collectionDesigns || collectionDesigns.length <= 1) return;
    if (!selectedItems || !selectedItems.length) return;
    if (!attributes || !Object.keys(attributes).length) return;

    const selectedElementsData = selectedItems.map(({ itemId, pageId }) => ({
      ...designData.elements[itemId],
      pageId
    }));

    const firstElement = selectedElementsData[0];
    if (!firstElement || !firstElement.type) return;

    const isHomogenous = selectedElementsData.every(
      el => el.type === firstElement.type
    );
    if (!isHomogenous) return;

    const allowlist = PROPAGATABLE_ATTRIBUTES_BY_TYPE[firstElement.type];
    if (!allowlist) return;

    const propagatable = Object.fromEntries(
      Object.entries(attributes).filter(([key]) => key in allowlist)
    );
    if (!Object.keys(propagatable).length) return;

    addActionToPropagationQueue({
      selectedElementsData,
      actionType: "elementAttributeChange",
      attributes: propagatable
    });
  },

  /**
   * Enqueue a `cssAnimationChange` action per animated element. Parallel to
   * enqueueAttributePropagation but dispatches a dedicated action type because
   * cssAnimation replay requires rebuilding the sibling page's
   * animatedElements map (not just writing the attribute). No homogeneity
   * gating is needed — every element type that supports animation goes through
   * the same handler.
   *
   * @param {object} elementUpdates - the element-keyed updates passed into
   *   onElementsAttributesChange. Entries without cssAnimation are skipped.
   * @param {object} updatedDesignData - the post-update design data, used to
   *   look up each element's type/uniqueId.
   * @param {object} containerProps - the container props carrying
   *   collectionDesigns + addActionToPropagationQueue.
   */
  enqueueCssAnimationPropagation(
    elementUpdates,
    updatedDesignData,
    containerProps
  ) {
    if (!containerProps || !containerProps.addActionToPropagationQueue) return;
    if (
      !containerProps.collectionDesigns ||
      containerProps.collectionDesigns.length <= 1
    ) {
      return;
    }

    Object.entries(elementUpdates).forEach(([elementId, update]) => {
      if (!update || !update.hasOwnProperty("cssAnimation")) return;

      const element = updatedDesignData.elements[elementId];
      if (!element || !element.type) return;

      // pageId usually rides on the update entry (onApplyAnimationPreset,
      // AnimationsTab both set it); fall back to the source design's page
      // lookup so future call sites that omit it still dispatch a valid
      // action. Without a pageId, matchElementInDesign cannot narrow to the
      // right page and the propagation silently no-ops on the sibling.
      const pageId =
        update.pageId ||
        (updatedDesignData.getElementPageId &&
          updatedDesignData.getElementPageId(elementId));

      containerProps.addActionToPropagationQueue({
        selectedElementsData: [
          {
            ...element,
            uniqueId: element.uniqueId || elementId,
            pageId
          }
        ],
        actionType: "cssAnimationChange",
        attributes: { cssAnimation: update.cssAnimation }
      });
    });
  },

  /**
   * Enqueue a `deleteElement` action for "Update Collection" propagation of a
   * structural delete. Parallel to enqueueAttributePropagation but dispatches
   * a dedicated action type because delete routes through
   * design.deleteElements on each sibling (handling animatedElements cleanup,
   * group-child cascade, and linked-caption cascade) rather than
   * updateElementsAttribute.
   *
   * CRITICAL: call this BEFORE mutating the source design. The helper
   * snapshots `uniqueId`, `type`, `pageId`, `groupId`, and the content-match
   * fields (`value`, `src`, `id`, `imageInstructions`) from the pre-delete
   * designData so matchElementInDesign's content fallback has the right keys
   * to resolve the sibling element. After the delete, those fields are gone
   * from `designData.elements`.
   *
   * NOTE on grouped deletes: the `groupId` we snapshot here is the SOURCE
   * design's group `uniqueId`. matchElementInDesign's content-fallback
   * compares sibling `element.groupId === originalElement.groupId` for strict
   * equality, and siblings have their own distinct group uniqueIds, so the
   * fallback will reject grouped candidates. Grouped-delete propagation
   * therefore depends on element uniqueId parity across siblings (the fast
   * path at collectionHelpers:12-17). For elements whose uniqueId diverges
   * between siblings — e.g. pre-#421 history or manually re-added elements —
   * grouped deletes silently no-op on the sibling. See
   * specs/2026-04-23-feature-update-collection-add-delete-propagation.md.
   */
  enqueueDeleteElementPropagation({
    elements,
    designData,
    collectionDesigns,
    addActionToPropagationQueue
  }) {
    if (!addActionToPropagationQueue) return;
    if (!collectionDesigns || collectionDesigns.length <= 1) return;
    if (!elements || !elements.length) return;
    if (!designData) return;

    const selectedElementsData = elements
      .map(({ itemId, pageId, groupId }) => {
        const element = designData.elements[itemId];
        if (!element || !element.type) return null;
        return {
          uniqueId: element.uniqueId || itemId,
          pageId,
          groupId: groupId || element.groupId || null,
          type: element.type,
          value: element.value,
          src: element.src,
          id: element.id,
          imageInstructions: element.imageInstructions
        };
      })
      .filter(Boolean);

    if (!selectedElementsData.length) return;

    addActionToPropagationQueue({
      selectedElementsData,
      actionType: "deleteElement"
    });
  },

  /**
   * Enqueue an `addElement` action for "Update Collection" propagation of a
   * structural add. Parallel to enqueueDeleteElementPropagation but covers
   * the inverse op: when an element is inserted on the source design, every
   * sibling receives a scaled copy on the corresponding page.
   *
   * Sibling matching keys off `element.uniqueId` — the source insert and the
   * sibling insert use the same id so subsequent attribute / animation
   * propagation continues to match. There is no content fallback for adds
   * (the element does not yet exist on the sibling), so callers MUST pass
   * an element that already has a `uniqueId`.
   *
   * The helper short-circuits on non-collection documents (collectionDesigns
   * length <= 1), so callers can invoke it unconditionally without first
   * checking `isDesignPartOfACollection`.
   *
   * Special-case caller note: the textmask flow in EditorElementOps.onInsertElement
   * does NOT enqueue an add — a textmask is an attribute change on an
   * existing textbox handled by the elementAttributeChange path. Place the
   * call AFTER the textmask early-return.
   *
   * @param {object} element - the element being inserted (must include
   *   `uniqueId`, `type`, geometry, and any `cssAnimation`).
   * @param {object} designData - the source design data BEFORE the insert,
   *   used by the handler to compute bleed-aware scale ratios.
   * @param {string} currentPageId - source page id; the handler maps it onto
   *   the sibling's pagesOrder index, falling back to page 0.
   * @param {Array} collectionDesigns - sibling designs in the same collection.
   * @param {Function} addActionToPropagationQueue - bound dispatch wrapper.
   */
  enqueueAddElementPropagation({
    element,
    designData,
    currentPageId,
    collectionDesigns,
    addActionToPropagationQueue
  }) {
    if (!addActionToPropagationQueue) return;
    if (!collectionDesigns || collectionDesigns.length <= 1) return;
    if (!element || !element.uniqueId) return;
    if (!designData || !currentPageId) return;

    addActionToPropagationQueue({
      element,
      designData,
      currentPageId,
      uniqueId: element.uniqueId,
      actionType: "addElement"
    });
  },

  /**
   * Enqueue an `elementGeometryChange` action for "Update Collection"
   * propagation of a source-side move or resize. Parallel to
   * enqueueAddElementPropagation but covers the geometry-change case: when a
   * user drags or resizes an element on the source, every unrestricted
   * sibling receives the same change remapped proportionally onto its
   * canvas.
   *
   * CRITICAL: call this BEFORE mutating the source design. The helper
   * snapshots the pre-mutation `originalElement` so
   * matchElementInDesign's content fallback has the right `value`/`src`/`id`
   * keys to resolve the sibling element — after the mutation the fields on
   * the source may have drifted.
   *
   * Pass `designData` (source canvas dims + bleed) AND the post-mutation
   * `newGeometry` for each entry. The handler runs the shared
   * `proportionalGeometry` util with `{ ...originalElement, ...newGeometry }`
   * and only applies the keys present in `newGeometry`, so a move-only
   * enqueue won't overwrite sibling width/height/scale.
   *
   * The helper short-circuits on non-collection documents (collectionDesigns
   * length <= 1), so callers can invoke it unconditionally without first
   * checking `isDesignPartOfACollection`.
   *
   * @param {object} designData - the source design data (pre-mutation).
   * @param {string} currentPageId - source page id (carried on the action
   *   for future page-index mapping; mirrors enqueueAddElementPropagation).
   * @param {Array} selectedElementsData - entries of the shape
   *   `{ uniqueId, pageId, type, originalElement, newGeometry }`.
   * @param {Array} collectionDesigns - sibling designs in the same
   *   collection.
   * @param {Function} addActionToPropagationQueue - bound dispatch wrapper.
   */
  enqueueGeometryPropagation({
    designData,
    currentPageId,
    selectedElementsData,
    collectionDesigns,
    addActionToPropagationQueue
  }) {
    if (!addActionToPropagationQueue) return;
    if (!collectionDesigns || collectionDesigns.length <= 1) return;
    if (!selectedElementsData || !selectedElementsData.length) return;
    if (!designData || !currentPageId) return;

    const validEntries = selectedElementsData.filter(
      entry =>
        entry &&
        entry.uniqueId &&
        entry.pageId &&
        entry.type &&
        entry.originalElement &&
        entry.newGeometry &&
        Object.keys(entry.newGeometry).length
    );
    if (!validEntries.length) return;

    addActionToPropagationQueue({
      actionType: "elementGeometryChange",
      designData,
      currentPageId,
      selectedElementsData: validEntries
    });
  }
};

export default ActionBarOps;
